// Google Ads Conversion Tracking

declare global {
  interface Window {
    gtag: (...args: unknown[]) => void;
    dataLayer: unknown[];
  }
}

export const GA_MEASUREMENT_ID = 'G-DLLNRDMCCV'; // GA4
export const GOOGLE_ADS_ID = 'AW-17797927530'; // Google Ads

// Conversion labels - Configure estes no Google Ads > Conversões > + Nova ação de conversão
export const CONVERSION_LABELS = {
  subscription: 'm7KTCMDHhdAbEOqk26ZC', // Assinatura concluída
  signup: 'sBYyCKqgx9AbEOqk26ZC', // Cadastro (Contato)
  checkout_start: 'kG8fCNOp1tAbEOqk26ZC', // Início de checkout
  page_view_pricing: '', // Visualização página de preços
  create_project: '', // Criação de projeto
  create_charge: '', // Criação de cobrança
  login: '', // Login bem-sucedido
};

// Track page views (GA4)
export const pageview = (url: string) => {
  if (typeof window.gtag !== 'undefined') {
    window.gtag('config', GA_MEASUREMENT_ID, {
      page_path: url,
    });
  }
};

// Track subscription purchase conversion (COMPRA CONCLUÍDA)
export const trackSubscriptionConversion = (tier: 'prime' | 'pro', value: number) => {
  if (typeof window.gtag !== 'undefined') {
    // Conversão principal com label (Google Ads)
    window.gtag('event', 'conversion', {
      send_to: `${GOOGLE_ADS_ID}/${CONVERSION_LABELS.subscription}`,
      value: value,
      currency: 'BRL',
      transaction_id: `${tier}_${Date.now()}`,
    });
    
    // Evento de purchase para analytics
    window.gtag('event', 'purchase', {
      currency: 'BRL',
      value: value,
      transaction_id: `${tier}_${Date.now()}`,
      items: [{
        item_id: tier,
        item_name: tier === 'prime' ? 'Taskeer Prime' : 'Taskeer PRO',
        price: value,
        quantity: 1,
      }],
    });
  }
};

// Track checkout initiation (INÍCIO DE CHECKOUT)
export const trackCheckoutStart = (tier: 'prime' | 'pro', value: number) => {
  if (typeof window.gtag !== 'undefined') {
    // Evento padrão de início de checkout
    window.gtag('event', 'begin_checkout', {
      currency: 'BRL',
      value: value,
      items: [{
        item_id: tier,
        item_name: tier === 'prime' ? 'Taskeer Prime' : 'Taskeer PRO',
        price: value,
        quantity: 1,
      }],
    });

    // Conversão para Google Ads (se label configurado)
    if (CONVERSION_LABELS.checkout_start) {
      window.gtag('event', 'conversion', {
        send_to: `${GOOGLE_ADS_ID}/${CONVERSION_LABELS.checkout_start}`,
        value: value,
        currency: 'BRL',
      });
    }
  }
};

// Track new user signup (CADASTRO)
export const trackSignup = () => {
  if (typeof window.gtag !== 'undefined') {
    // Evento padrão de cadastro
    window.gtag('event', 'sign_up', {
      method: 'email',
    });
    
    // Conversão para Google Ads (se label configurado)
    if (CONVERSION_LABELS.signup) {
      window.gtag('event', 'conversion', {
        send_to: `${GOOGLE_ADS_ID}/${CONVERSION_LABELS.signup}`,
      });
    }
  }
};

// Track login (LOGIN)
export const trackLogin = () => {
  if (typeof window.gtag !== 'undefined') {
    window.gtag('event', 'login', {
      method: 'email',
    });

    if (CONVERSION_LABELS.login) {
      window.gtag('event', 'conversion', {
        send_to: `${GOOGLE_ADS_ID}/${CONVERSION_LABELS.login}`,
      });
    }
  }
};

// Track pricing page view (VISUALIZAÇÃO PÁGINA DE PREÇOS)
export const trackPricingPageView = () => {
  if (typeof window.gtag !== 'undefined') {
    window.gtag('event', 'view_item_list', {
      item_list_id: 'subscription_plans',
      item_list_name: 'Planos de Assinatura',
      items: [
        { item_id: 'prime', item_name: 'Taskeer Prime', price: 29.99 },
        { item_id: 'pro', item_name: 'Taskeer PRO', price: 67.90 },
      ],
    });

    if (CONVERSION_LABELS.page_view_pricing) {
      window.gtag('event', 'conversion', {
        send_to: `${GOOGLE_ADS_ID}/${CONVERSION_LABELS.page_view_pricing}`,
      });
    }
  }
};

// Track project creation (CRIAÇÃO DE PROJETO)
export const trackProjectCreation = () => {
  if (typeof window.gtag !== 'undefined') {
    window.gtag('event', 'generate_lead', {
      event_category: 'engagement',
      event_label: 'project_created',
    });

    if (CONVERSION_LABELS.create_project) {
      window.gtag('event', 'conversion', {
        send_to: `${GOOGLE_ADS_ID}/${CONVERSION_LABELS.create_project}`,
      });
    }
  }
};

// Track charge creation (CRIAÇÃO DE COBRANÇA PIX)
export const trackChargeCreation = (value: number) => {
  if (typeof window.gtag !== 'undefined') {
    window.gtag('event', 'add_payment_info', {
      currency: 'BRL',
      value: value,
    });

    if (CONVERSION_LABELS.create_charge) {
      window.gtag('event', 'conversion', {
        send_to: `${GOOGLE_ADS_ID}/${CONVERSION_LABELS.create_charge}`,
        value: value,
        currency: 'BRL',
      });
    }
  }
};

// Track subscription plan selection (antes do checkout)
export const trackPlanSelect = (tier: 'prime' | 'pro', value: number) => {
  if (typeof window.gtag !== 'undefined') {
    window.gtag('event', 'add_to_cart', {
      currency: 'BRL',
      value: value,
      items: [{
        item_id: tier,
        item_name: tier === 'prime' ? 'Taskeer Prime' : 'Taskeer PRO',
        price: value,
        quantity: 1,
      }],
    });
  }
};

// Track CTA clicks on landing page
export const trackCTAClick = (ctaName: string, location: string) => {
  if (typeof window.gtag !== 'undefined') {
    window.gtag('event', 'cta_click', {
      event_category: 'engagement',
      event_label: ctaName,
      cta_location: location,
    });
  }
};

// Track blog post view (SEO Analytics)
export const trackBlogPostView = (postData: {
  slug: string;
  title: string;
  category: string;
  readTimeMinutes: number;
}) => {
  if (typeof window.gtag !== 'undefined') {
    window.gtag('event', 'view_item', {
      content_type: 'blog_post',
      content_id: postData.slug,
      items: [{
        item_id: postData.slug,
        item_name: postData.title,
        item_category: postData.category,
      }],
    });
    
    // Custom event for blog analytics
    window.gtag('event', 'blog_post_view', {
      post_slug: postData.slug,
      post_title: postData.title,
      post_category: postData.category,
      read_time_minutes: postData.readTimeMinutes,
    });
  }
};

// Track blog scroll depth
export const trackBlogScrollDepth = (slug: string, depth: number) => {
  if (typeof window.gtag !== 'undefined') {
    window.gtag('event', 'scroll', {
      content_type: 'blog_post',
      content_id: slug,
      percent_scrolled: depth,
    });
  }
};

// Track blog post share
export const trackBlogShare = (slug: string, method: string) => {
  if (typeof window.gtag !== 'undefined') {
    window.gtag('event', 'share', {
      content_type: 'blog_post',
      content_id: slug,
      method: method,
    });
  }
};

// Track blog list view
export const trackBlogListView = (category?: string) => {
  if (typeof window.gtag !== 'undefined') {
    window.gtag('event', 'view_item_list', {
      item_list_id: 'blog_posts',
      item_list_name: category ? `Blog - ${category}` : 'Blog - Todos',
    });
  }
};

// Track blog search
export const trackBlogSearch = (searchTerm: string, resultsCount: number) => {
  if (typeof window.gtag !== 'undefined') {
    window.gtag('event', 'search', {
      search_term: searchTerm,
      content_type: 'blog',
      results_count: resultsCount,
    });
  }
};
